import CardCompanyEntitiesGraph from "@/components/company/card-company-entities-graph";
import CardCompanyLegalIdentity from "@/components/company/card-company-legal-identity";
import CardCompanyRelationshipGraph from "@/components/company/card-company-relationship-graph";
import { FadeInSection } from "@/components/company/company-animations";
import CardDomainAliases from "@/components/company/card-domain-aliases";
import CompanySectionCard, {
  CompanyCardSkeleton,
} from "@/components/company/company-section-card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  useAreSectionsCutByBudget,
  useAreSectionsFailed,
  useAreSectionsPending,
} from "@/contexts/company-sections-context";
import { useTranslation } from "@/hooks/useTranslation";
import { COMPANY_CARD_SECTIONS } from "@/lib/company-card-sections";
import {
  hasCertificationData,
  hasDomainAliasesData,
  hasEstablishmentsData,
  hasLegalIdentityData,
  hasRelationshipsData,
} from "@/lib/company-data-availability";
import { AlertTriangleIcon, PencilIcon } from "lucide-react";
import { useCompanyTabContext } from "./company-tab-context";

export default function CompanyAdministrativePage() {
  const { t } = useTranslation();
  const { company, onEditSources } = useCompanyTabContext();

  const business = company.business;
  const hasSiret = !!(business.siretFormatted || business.siret);

  const showDomainAliases = hasDomainAliasesData(company);
  // The registry section is what identifies the company: until it ran, a
  // missing SIRET means "not looked up yet" — still running, cut by the
  // spent budget the page's banner names, or failed (the registry or the run
  // down) — not "unidentified".
  const isLegalPending = useAreSectionsPending(
    COMPANY_CARD_SECTIONS.legalIdentity,
  );
  const isLegalCutByBudget = useAreSectionsCutByBudget(
    COMPANY_CARD_SECTIONS.legalIdentity,
  );
  const isLegalFailed = useAreSectionsFailed(
    COMPANY_CARD_SECTIONS.legalIdentity,
  );
  const isUnidentified =
    !hasSiret && !isLegalPending && !isLegalCutByBudget && !isLegalFailed;

  return (
    <FadeInSection>
      <div className="flex flex-col gap-4 @container sm:gap-5">
        {isUnidentified && (
          <Alert variant="warning">
            <AlertTriangleIcon />
            <AlertTitle>
              {t("company.administrative.unidentifiedWarning.title")}
            </AlertTitle>
            <AlertDescription>
              <p>
                {t("company.administrative.unidentifiedWarning.description")}
              </p>
              <Button
                variant="warning"
                size="sm"
                className="mt-2"
                onClick={onEditSources}
              >
                <PencilIcon className="w-4 h-4" />
                {t("company.administrative.unidentifiedWarning.action")}
              </Button>
            </AlertDescription>
          </Alert>
        )}
        <CompanySectionCard
          sections={COMPANY_CARD_SECTIONS.relationshipGraph}
          hasData={hasRelationshipsData(company)}
          skeleton={<CompanyCardSkeleton className="min-h-[240px]" />}
        >
          <CardCompanyRelationshipGraph company={company} />
        </CompanySectionCard>
        {/* An unidentified company keeps its placeholder card, with "—". */}
        <CompanySectionCard
          sections={COMPANY_CARD_SECTIONS.legalIdentity}
          hasData={
            hasLegalIdentityData(company) ||
            hasCertificationData(company) ||
            isUnidentified
          }
          skeleton={<CompanyCardSkeleton />}
        >
          <CardCompanyLegalIdentity
            company={company}
            showAllFields={isUnidentified}
          />
        </CompanySectionCard>
        {showDomainAliases && <CardDomainAliases company={company} />}
        <CompanySectionCard
          sections={COMPANY_CARD_SECTIONS.establishments}
          hasData={hasEstablishmentsData(company)}
          skeleton={<CompanyCardSkeleton />}
        >
          <CardCompanyEntitiesGraph company={company} />
        </CompanySectionCard>
      </div>
    </FadeInSection>
  );
}
